import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { withContent } from '@bubbletea/core';
import { currencyFormatter } from '@mooncake/utils';
import { DurationTypes, IContent } from '../../../../types';
import { PlansItem } from '../../../../types/generatedContent';
import Button from '../../../UI/Button';
import { DropDownIcon, DiscountOtherIcon, DiscountRedIcon, LockIcon, UbiplusIcon } from '../../../UI/Icons';
import Feature from './Feature';
import {
  BonusTitle,
  Content,
  MainContentWrapper,
  SecondaryContentWrapper,
  Duration,
  FeaturesContainer,
  FeaturesSection,
  HeaderSection,
  PlanCardWrapper,
  PlanDetails,
  SaveContent,
  UbiplusIconSection,
  UbiplusSection,
  SaveContainer,
  Price,
  ButtonLoaderContainer,
} from './styles';
import { IStoreState } from '../../../../types/store';
import { IIbexPricingDetail } from '../../../../types/subscription';
import sendEvent from '../../../../utils/sendEvent';
import { IAdobeProductClickEvent } from '../../../../types/tracking';
import Loader from '../../../../core/UI/Loader';

interface IPlansProps {
  context: IContent;
  plan: PlansItem;
  ibexPricingDetail?: IIbexPricingDetail;
  oneMonthSubscription?: IIbexPricingDetail | null;
  locale: string;
  index: number;
}

const PlanCard = ({ context, plan, ibexPricingDetail, oneMonthSubscription, locale, index }: IPlansProps) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    window.addEventListener('load', () => {
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    });

    return () => {
      window.removeEventListener('load', () => {
        setIsLoading(false);
      });
    };
  }, []);

  const contentSelector = useSelector((state: IStoreState) => ({
    currency: state.coreApp.currency,
    // ageRestricted: state.app.ageRestricted,
  }));
  const { currency } = contentSelector;
  const { googlesheet } = context;
  const { global, plans } = googlesheet;
  const durationFormatted = plan.id?.replace(/[^0-9]/g, '');
  const duration = durationFormatted ? +durationFormatted : 1;
  const is12Months = duration === 12;
  const isNot1Month = duration !== 1;
  const [showPlanDetails, setShowPlanDetails] = useState(false);
  const price = ibexPricingDetail?.basePrice;
  const priceOneMonth = oneMonthSubscription?.basePrice;
  const currencyValue = currency || ibexPricingDetail?.currency;

  const pricePerMonth = isNot1Month && price ? parseFloat((price / duration).toFixed(2)) : price;

  const percentageOff = pricePerMonth && priceOneMonth ? ((priceOneMonth - pricePerMonth) / priceOneMonth) * 100 : 0;

  const saveText = locale.indexOf('zh') > -1 ? plan.save : plans.save?.replace('XX', percentageOff.toFixed(0));

  let freeTrialDuration;
  const freeTrial = ibexPricingDetail?.promotions?.filter((promotion) => promotion.type === 'freeTrial');

  if (freeTrial?.length === 1 && freeTrial[0].durationUnit) {
    freeTrialDuration = `${freeTrial[0].duration} ${global[freeTrial[0].durationUnit as DurationTypes]}`;
  }

  const priceBilled =
    freeTrialDuration && duration === 1
      ? plans.priceBilledAfterTrialOneMonth
      : freeTrialDuration
      ? plans.priceBilledAfterTrial
      : plans.priceBilled;

  const toggleShowPlanDetails = () => {
    setShowPlanDetails(!showPlanDetails);
  };

  const priceBilledFormatted =
    duration > 1 && price && priceBilled && priceBilled
      ? priceBilled
          .replace('{price}', currencyFormatter(+price, locale, currencyValue as string))
          .replace('{duration}', duration.toString())
      : '&nbsp;';

  const learnMoreEventDetails = {
    location: 'available plans',
    category: 'interaction',
    locationPosition: 4,
    action: 'ubisoft plus : learn more',
  };

  const sendAddToCartEvent = () => {
    if (ibexPricingDetail) {
      const promotionInfo = ibexPricingDetail.promotions?.length ? ibexPricingDetail.promotions[0] : null;
      const eventDetails: IAdobeProductClickEvent = {
        productId: ibexPricingDetail.id,
        productName: ibexPricingDetail.name,
        productEdition: ibexPricingDetail.name,
        productSalesType: 'subscription',
        location: 'available plans',
        category: 'cart',
        locationPosition: 4,
        isCartAdd: 1,
        isCartOpen: 1,
        productDiscount: 'no',
        action: 'rocksmith plus : add to cart',
      };
      if (promotionInfo) {
        eventDetails.productDiscountName = promotionInfo.promotionName;
        eventDetails.productDiscount = 'yes';
      }
      sendEvent(eventDetails, 'clickevent');
    }
  };

  return (
    <PlanCardWrapper
      color={plan.color}
      is12Months={is12Months}
      initial={{
        opacity: 0,
        x: -3,
      }}
      whileInView={{
        opacity: 1,
        x: 0,
      }}
      transition={{
        type: 'spring',
        delay: 0.2 + index * 0.1,
      }}
      viewport={{ once: true }}
    >
      <MainContentWrapper>
        <SaveContainer is12Months={is12Months}>
          {isNot1Month && percentageOff && percentageOff > 0 ? (
            <>
              <SaveContent
                is12Months={is12Months}
                dangerouslySetInnerHTML={{ __html: saveText as string }}
                isZH={locale.indexOf('zh') > -1}
              />
              {is12Months ? <DiscountRedIcon /> : <DiscountOtherIcon />}
            </>
          ) : null}
        </SaveContainer>
        <Content isNot1Month={isNot1Month}>
          <HeaderSection>
            {/*{freeTrialDuration ? (*/}
            {/*  <FreeTrialNotice>{plans.freeTrial?.replace('{duration}', freeTrialDuration)}</FreeTrialNotice>*/}
            {/*) : null}*/}
            <Duration color={plan.color}>{plan.id}</Duration>
            {pricePerMonth ? (
              <>
                <Price
                  dangerouslySetInnerHTML={{
                    __html: plans.pricePerMonth.replace(
                      '{price}',
                      `<span>${currencyFormatter(+pricePerMonth, locale, currencyValue as string)}</span>`
                    ),
                  }}
                />
                <span dangerouslySetInnerHTML={{ __html: priceBilledFormatted }} />
              </>
            ) : null}
          </HeaderSection>
          {isLoading ? (
            <ButtonLoaderContainer>
              <Loader over={false} />
            </ButtonLoaderContainer>
          ) : pricePerMonth ? (
            <Button
              classNames={['subscribe-plans-ctas', 'ibex-subscribe']}
              subscriptionId={ibexPricingDetail?.id}
              key={`subscribe-${plan.id}-cta`}
              type="subscription"
              // disabled={ageRestricted}
              // tooltipContent={ageRestricted && `${global.ageGate}<br>${global.privacyPolicy}`}
              handleClick={() => sendAddToCartEvent()}
            >
              {freeTrialDuration ? global.tryForFree : global.subscribeNow}
            </Button>
          ) : null}
          <PlanDetails isOpen={showPlanDetails} onClick={toggleShowPlanDetails}>
            {global.planDetails}
            <DropDownIcon />
          </PlanDetails>
          <FeaturesContainer showPlanDetails={showPlanDetails}>
            <FeaturesSection>
              {freeTrialDuration ? (
                <Feature
                  key={'freeTrialInfo'}
                  text={plans.freeTrial?.replace('{duration}', freeTrialDuration) as string}
                  color={plan.color}
                  isFreeTrialFeature={!!freeTrialDuration}
                />
              ) : null}
              {plan.features.map((feature) => (
                <Feature key={feature} text={feature as string} />
              ))}
            </FeaturesSection>
            {plan?.ubisoftPlusDetailsVisible && plan.ubisoftPlusDetails && (
              <UbiplusSection>
                <UbiplusIconSection>
                  <UbiplusIcon />
                  <a href="#promo_offers" onClick={() => sendEvent(learnMoreEventDetails)}>
                    {global.learnMore}
                  </a>
                </UbiplusIconSection>
                <FeaturesSection>
                  <Feature text={plan.ubisoftPlusDetails} />
                </FeaturesSection>
              </UbiplusSection>
            )}
          </FeaturesContainer>
        </Content>
      </MainContentWrapper>
      {plan.bonus && (
        <SecondaryContentWrapper style={{ marginTop: '1rem' }} showPlanDetails={showPlanDetails}>
          <BonusTitle>
            <LockIcon />
            <span>{plan.bonus.title}</span>
          </BonusTitle>
          <FeaturesSection padding={2}>
            {plan.bonus.features.map((feature) => (
              <Feature color={plan.color} learnMore="#promo_offers" key={feature} text={feature as string} />
            ))}
          </FeaturesSection>
        </SecondaryContentWrapper>
      )}
    </PlanCardWrapper>
  );
};

export default withContent(PlanCard);
